import type { MutableRefObject } from 'react';
import type { ReportSubmissionResponse } from './supportToast';

export type ExistingReport = {
  report_id: number; reference: string; status: string; created_at: string;
  updated_at?: string | null; summary: string; follow_up_eligible: boolean;
  follow_up_blocked_reason?: string | null;
};
export type ReportIntent = { new_issue: true; follow_up_of?: never } | { follow_up_of: number; new_issue?: never };
export type ReportAttempt = { body: string } | null;
export class ReportSubmitError extends Error {
  constructor(message: string, public retryOnly = false) { super(message); }
}

/** Retain the entire request, including diagnostic timestamps, after an uncertain response. */
export async function submitReport(
  payload: Record<string, unknown>, attempt: MutableRefObject<ReportAttempt>,
): Promise<ReportSubmissionResponse> {
  if (!attempt.current) {
    attempt.current = { body: JSON.stringify({ ...payload, client_request_id: makeReportRequestId() }) };
  }
  let response: Response;
  try {
    response = await fetch('/api/report_problem', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: attempt.current.body,
    });
  } catch {
    throw new ReportSubmitError('The result is not confirmed. Retry this same submission to check it without creating a duplicate.', true);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    // A server/transport failure may occur after the transaction committed.
    if (response.status >= 500) throw new ReportSubmitError('The result is not confirmed. Retry this same submission.', true);
    attempt.current = null;
    const detail = data?.detail;
    throw new ReportSubmitError(typeof detail === 'string' ? detail : detail?.message
      || 'This case cannot accept the report. Refresh the cases or choose a new issue; your draft is preserved.');
  }
  if (!data?.report_id) throw new ReportSubmitError('The saved case could not be confirmed. Retry this same submission.', true);
  if (data.explicit && !data.coalesced && data.delivery === 'pending') {
    throw new ReportSubmitError([
      `Case ${data.reference || data.report_id} is recorded; email delivery is still unconfirmed. Retry this same submission to check its outcome.`,
      data.safety_notice,
    ].filter(Boolean).join(' '), true);
  }
  attempt.current = null;
  if (data.explicit && !data.coalesced && data.delivery === 'failed') {
    const reference = data.reference || `SUP-${String(data.report_id).padStart(6, '0')}`;
    throw new ReportSubmitError([
      `Case ${reference} is recorded, but email delivery failed. Your draft and any screenshot are preserved here.`,
      data.message,
      'Choosing Submit new report will create another case and make a new delivery attempt; nothing is resent automatically.',
      data.safety_notice,
    ].filter(Boolean).join(' '));
  }
  return data;
}

/** Backend timestamps may be naive UTC or carry an explicit UTC/offset suffix. */
export function formatReportTimestamp(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
}

/** Request identity, not an authentication token; older browsers still need a valid UUID. */
export function makeReportRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* use the compatible path */ }
  const bytes = new Uint8Array(16);
  try {
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') throw new Error();
    crypto.getRandomValues(bytes);
  } catch {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
